import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SmartLink } from "@/components/SmartLink";
import { Contours } from "@/components/Contours";
import { CONTACT_ENDPOINT, CONTACT_EMAIL, CONTACT_PHONE, COMPANY } from "@/data/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const Field = ({ label, error, children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="mb-2 flex items-baseline justify-between text-sm font-medium text-[var(--ink)]">
      {label}
      {error && <span className="text-xs font-normal text-[#e07a6a]">{error}</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full rounded-xl border border-[var(--line-strong)] bg-[var(--surface)] px-4 py-3 text-base text-[var(--ink)] placeholder:text-[var(--ink-3)] outline-none transition-colors focus:border-[var(--sage)] focus:ring-2 focus:ring-[var(--olive)]/30";

export const ContactPage = () => {
  const { t, lang } = useLanguage();
  const c = t.contact;
  const f = c.form;
  const [values, setValues] = useState({ name: "", email: "", topic: f.topics[0].value, message: "", consent: false, website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  usePageMeta({ title: t.meta.contactTitle, description: t.meta.contactDescription, path: "/kontakt", lang });

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((s) => ({ ...s, [k]: v }));
    setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!values.name.trim()) err.name = f.required;
    if (!EMAIL_RE.test(values.email.trim())) err.email = f.invalidEmail;
    if (values.message.trim().length < 10) err.message = f.tooShort;
    if (!values.consent) err.consent = f.consentRequired;
    return err;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length) return;
    setStatus("sending");
    try {
      const res = await fetch(CONTACT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          topic: values.topic,
          message: values.message.trim(),
          consent: values.consent,
          website: values.website, // honeypot – must stay empty
          lang,
          page: window.location.href,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setStatus("success");
    } catch (error) {
      setStatus("error");
    }
  };

  const reset = () => {
    setValues({ name: "", email: "", topic: f.topics[0].value, message: "", consent: false, website: "" });
    setErrors({});
    setStatus("idle");
  };

  return (
    <section className="relative">
      <div className="absolute inset-x-0 top-0 h-[520px] overflow-hidden" aria-hidden="true">
        <Contours rings={14} step={56} seed={21} cx={300} cy={260} opacity={0.12} />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-[var(--bg)]" />
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-32 sm:px-6 md:px-8 md:pt-40 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">{c.eyebrow}</p>
          <h1 className="font-display mt-5 text-4xl leading-[1.05] sm:text-5xl lg:text-[3.6rem]">{c.title}</h1>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[var(--ink-2)] md:text-lg">{c.subtitle}</p>

          <div className="mt-12">
            <p className="eyebrow !text-[0.62rem] text-[var(--ink-3)]">{c.directTitle}</p>
            <ul className="mt-5 space-y-4">
              <li>
                <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)] transition-colors group-hover:border-[var(--sage)]/60">
                    <Mail className="h-5 w-5 text-[var(--sage)]" />
                  </span>
                  <span className="text-[var(--ink)] transition-colors group-hover:text-white">{CONTACT_EMAIL}</span>
                </a>
              </li>
              <li>
                <a href={`tel:+49${CONTACT_PHONE.replace(/^0/, "").replace(/\D/g, "")}`} data-testid="contact-phone-link" className="group flex items-center gap-4">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)] transition-colors group-hover:border-[var(--sage)]/60">
                    <Phone className="h-5 w-5 text-[var(--sage)]" />
                  </span>
                  <span className="text-[var(--ink)] transition-colors group-hover:text-white">{CONTACT_PHONE}</span>
                </a>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--line)] bg-[var(--surface)]">
                  <MapPin className="h-5 w-5 text-[var(--sage)]" />
                </span>
                <span className="text-sm leading-relaxed text-[var(--ink-2)]">
                  {COMPANY.name}
                  <br />
                  {COMPANY.street}
                  <br />
                  {COMPANY.city}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="card relative overflow-hidden p-6 sm:p-8 md:p-10">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex min-h-[420px] flex-col items-center justify-center text-center"
                  data-testid="contact-success"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--olive)]/20">
                    <CheckCircle2 className="h-8 w-8 text-[var(--sage)]" />
                  </span>
                  <h2 className="font-display mt-6 text-3xl">{f.successTitle}</h2>
                  <p className="mt-3 max-w-sm text-[var(--ink-2)]">{f.successBody}</p>
                  <button type="button" onClick={reset} className="btn btn-ghost mt-8 px-6 py-3 text-sm" data-testid="contact-again">
                    {f.again}
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onSubmit={onSubmit}
                  noValidate
                  className="space-y-6"
                  data-testid="contact-form"
                >
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label={f.name} error={errors.name} htmlFor="contact-name">
                      <input id="contact-name" name="name" type="text" autoComplete="name" placeholder={f.namePh} value={values.name} onChange={set("name")} className={inputCls} data-testid="contact-name" />
                    </Field>
                    <Field label={f.email} error={errors.email} htmlFor="contact-email">
                      <input id="contact-email" name="email" type="email" autoComplete="email" placeholder={f.emailPh} value={values.email} onChange={set("email")} className={inputCls} data-testid="contact-email" />
                    </Field>
                  </div>

                  <Field label={f.topic} htmlFor="contact-topic">
                    <select id="contact-topic" name="topic" value={values.topic} onChange={set("topic")} className={`${inputCls} appearance-none`} data-testid="contact-topic">
                      {f.topics.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label={f.message} error={errors.message} htmlFor="contact-message">
                    <textarea id="contact-message" name="message" rows={6} placeholder={f.messagePh} value={values.message} onChange={set("message")} className={`${inputCls} resize-y`} data-testid="contact-message" />
                  </Field>

                  {/* honeypot */}
                  <div className="absolute -left-[9999px] top-0" aria-hidden="true">
                    <label htmlFor="contact-website">Website</label>
                    <input id="contact-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
                  </div>

                  <label className="flex items-start gap-3 text-sm text-[var(--ink-2)]">
                    <input type="checkbox" checked={values.consent} onChange={set("consent")} className="mt-1 h-4 w-4 shrink-0 accent-[var(--olive)]" data-testid="contact-consent" />
                    <span>
                      {f.consent}{" "}
                      <SmartLink href="/datenschutz" className="underline decoration-[var(--sage)]/60 underline-offset-2 hover:text-white">
                        {f.consentLink}
                      </SmartLink>{" "}
                      {f.consentTail}
                      {errors.consent && <span className="block pt-1 text-xs text-[#e07a6a]">{errors.consent}</span>}
                    </span>
                  </label>

                  {status === "error" && (
                    <div className="flex items-start gap-3 rounded-xl border border-[#e07a6a]/40 bg-[#e07a6a]/10 px-4 py-3 text-sm" data-testid="contact-error">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#e07a6a]" />
                      <div>
                        <p className="font-medium text-[var(--ink)]">{f.errorTitle}</p>
                        <p className="text-[var(--ink-2)]">
                          {f.errorBody}{" "}
                          <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
                            {CONTACT_EMAIL}
                          </a>
                        </p>
                      </div>
                    </div>
                  )}

                  <button type="submit" disabled={status === "sending"} className="btn btn-primary w-full px-6 py-4 text-base disabled:opacity-60 sm:w-auto" data-testid="contact-submit">
                    {status === "sending" ? <Loader2 className="h-4.5 w-4.5 animate-spin" /> : <Send className="h-4.5 w-4.5" />}
                    {status === "sending" ? f.sending : f.submit}
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
